import { useEffect, useState, useCallback } from "react";
import { FileSignature, Download, FileSpreadsheet, Send, Loader2, Search, CheckCircle2, AlertCircle, ExternalLink, Trash2 } from "lucide-react";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Téléchargement authentifié d'un fichier renvoyé par l'API. */
async function downloadFile(url, fallbackName) {
  const res = await authFetch(url);
  if (!res.ok) throw new Error("Téléchargement impossible");
  const cd = res.headers.get("content-disposition") || "";
  const m = cd.match(/filename="?([^"]+)"?/);
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = m ? m[1] : fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 4000);
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}

/**
 * Onglet admin « Conventions » : registre des conventions de bêta-test
 * signées sur /convention (ligne de registre + PDF + statut de l'e-mail).
 */
export default function ConventionsPanel({ canDelete = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/admin/conventions`);
      if (res.ok) setItems(await res.json());
    } catch { toast.error("Chargement du registre impossible"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const exportXlsx = async () => {
    setBusy("export");
    try { await downloadFile(`${API_BASE}/admin/conventions/export.xlsx`, "Registre-conventions.xlsx"); }
    catch (e) { toast.error(e.message); }
    setBusy(null);
  };

  const getPdf = async (c) => {
    setBusy(`pdf-${c.reference}`);
    try { await downloadFile(`${API_BASE}/admin/conventions/${encodeURIComponent(c.reference)}/pdf`, c.fichier || `${c.reference}.pdf`); }
    catch (e) { toast.error(e.message); }
    setBusy(null);
  };

  const resend = async (c) => {
    if (!window.confirm(`Renvoyer la convention signée à ${c.email} (copie BIOMYR) ?`)) return;
    setBusy(`mail-${c.reference}`);
    try {
      const res = await authFetch(`${API_BASE}/admin/conventions/${encodeURIComponent(c.reference)}/resend`, { method: "POST" });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { toast.success(`Convention renvoyée à ${c.email}`); await load(); }
      else toast.error(typeof d.detail === "string" ? d.detail : "Envoi impossible");
    } catch { toast.error("Erreur de connexion"); }
    setBusy(null);
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer définitivement la convention de « ${c.exploitation} » (${c.reference}) et son PDF ?`)) return;
    setBusy(`del-${c.reference}`);
    try {
      const res = await authFetch(`${API_BASE}/admin/conventions/${encodeURIComponent(c.reference)}`, { method: "DELETE" });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success("Convention supprimée");
        setItems((prev) => prev.filter((x) => x.reference !== c.reference));
      } else toast.error(typeof d.detail === "string" ? d.detail : "Suppression impossible");
    } catch { toast.error("Erreur de connexion"); }
    setBusy(null);
  };

  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? items.filter((c) => [c.exploitation, c.signataire, c.email, c.siret, c.reference, c.ligne?.Commune, c.collected_by?.name, c.collected_by?.email]
        .some((v) => (v || "").toLowerCase().includes(needle)))
    : items;
  const unsent = items.filter((c) => !c.email_sent).length;

  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="admin-conventions">
      <div className="rounded-xl border border-black/5 bg-white p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 mr-auto">
          <FileSignature className="w-5 h-5" style={{ color: "#2D6A4F" }} />
          <div>
            <p className="text-sm font-bold" style={{ color: "#1A1A1A" }}>Registre des conventions</p>
            <p className="text-[11px]" style={{ color: "#7A7A7A" }}>
              {items.length} convention{items.length > 1 ? "s" : ""} signée{items.length > 1 ? "s" : ""}
              {unsent > 0 && <span style={{ color: "#B45309" }}> · {unsent} e-mail{unsent > 1 ? "s" : ""} non envoyé{unsent > 1 ? "s" : ""}</span>}
            </p>
          </div>
        </div>
        <a href="/convention" target="_blank" rel="noreferrer"
          className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium border border-black/10 hover:bg-black/5" style={{ color: "#4A4A4A" }}>
          <ExternalLink className="w-3.5 h-3.5" /> Faire signer une convention
        </a>
        <button onClick={exportXlsx} disabled={busy === "export" || !items.length}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
          style={{ background: "#2D6A4F" }} data-testid="conventions-export">
          {busy === "export" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
          Exporter Excel
        </button>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#9A948A" }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (exploitation, signataire, e-mail, SIRET, référence…)"
          className="w-full rounded-xl border border-black/10 bg-white pl-9 pr-3 py-2.5 text-sm outline-none" />
      </div>

      <div className="rounded-xl border border-black/5 bg-white overflow-x-auto">
        {loading ? (
          <div className="py-14 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#2D6A4F" }} /></div>
        ) : filtered.length === 0 ? (
          <p className="py-14 text-center text-sm" style={{ color: "#7A7A7A" }}>
            {items.length ? "Aucune convention ne correspond à la recherche." : "Aucune convention signée pour l'instant."}
          </p>
        ) : (
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b" style={{ borderColor: "#E5E5E0", color: "#7A7A7A" }}>
                {["Signée le", "Exploitation", "Signataire", "E-mail", "Recueillie par", "Référence", "E-mail envoyé", ""].map((h) => (
                  <th key={h} className="px-3 py-2.5 font-semibold uppercase tracking-wider text-[10px] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.reference} className="border-b last:border-0" style={{ borderColor: "#F0F0EC" }}>
                  <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: "#4A4A4A" }}>{formatDate(c.signed_at)}</td>
                  <td className="px-3 py-2.5">
                    <p className="font-semibold" style={{ color: "#1A1A1A" }}>{c.exploitation || "—"}</p>
                    <p className="text-[10px]" style={{ color: "#9A948A" }}>{c.siret ? `SIRET ${c.siret}` : ""}{c.ligne?.Commune ? ` · ${c.ligne.Commune}` : ""}</p>
                  </td>
                  <td className="px-3 py-2.5" style={{ color: "#4A4A4A" }}>
                    {c.signataire || "—"}
                    {c.ligne?.["Qualité"] && <span className="block text-[10px]" style={{ color: "#9A948A" }}>{c.ligne["Qualité"]}</span>}
                  </td>
                  <td className="px-3 py-2.5" style={{ color: "#4A4A4A" }}>{c.email}</td>
                  <td className="px-3 py-2.5" style={{ color: "#4A4A4A" }}>
                    {c.collected_by ? (c.collected_by.name || c.collected_by.email) : <span style={{ color: "#9A948A" }}>En autonomie</span>}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-[10px]" style={{ color: "#7A7A7A" }}>{c.reference}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    {c.email_sent ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: "#2D6A4F" }}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Oui
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: "#B45309" }}>
                        <AlertCircle className="w-3.5 h-3.5" /> Non
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button onClick={() => getPdf(c)} disabled={busy === `pdf-${c.reference}`} title="Télécharger le PDF"
                        className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium border border-black/10 hover:bg-black/5" style={{ color: "#2D6A4F" }}>
                        {busy === `pdf-${c.reference}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PDF
                      </button>
                      <button onClick={() => resend(c)} disabled={busy === `mail-${c.reference}`} title="Renvoyer l'e-mail au signataire"
                        className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium border border-black/10 hover:bg-black/5" style={{ color: "#4A4A4A" }}>
                        {busy === `mail-${c.reference}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Renvoyer
                      </button>
                      {canDelete && <button onClick={() => remove(c)} disabled={busy === `del-${c.reference}`} title="Supprimer la convention"
                        aria-label={`Supprimer la convention ${c.reference}`}
                        className="p-1.5 rounded-lg border border-black/10 hover:bg-red-50" style={{ color: "#EF4444" }}
                        data-testid={`convention-delete-${c.reference}`}>
                        {busy === `del-${c.reference}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
